// src/sizing/kvSizing.ts

import { MC_CURVE_PERCENTILES, type McCurvePercentile } from '@/types/percentiles';
import type { NewSizingRequest } from './newSizingEngine';

const BYTES_PER_GIB = 1024 ** 3;

// Minimal shape of what we need from the Monte Carlo summary
export interface McSummaryLike {
  p50Active: number;
  p95Active: number;
  activePercentiles?: Partial<Record<McCurvePercentile, number>>;
  personas: Array<{
    personaId: string;
    inputTokensPerConversation: number;
    outputTokensPerConversation: number;
    // 0 to 1, sum near 1
    shareOfTraffic: number;
  }>;
}

export interface KvSizingResult {
  kvSessionsP50: number;
  kvSessionsP95: number;
  kvSessionsByPercentile: Record<McCurvePercentile, number>;
  avgTokensPerConversation: number;
  kvBytesPerToken: number;
  kvGiBP50: number;
  kvGiBP95: number;
  kvCapacityGiB: number;
  kvGpusP50: number;
  kvGpusP95: number;
  kvGiBByPercentile: Record<McCurvePercentile, number>;
  kvGpusByPercentile: Record<McCurvePercentile, number>;
}

/**
 * Helper that computes:
 *  - p50 and p95 KV "sessions" from MC actives
 *  - average tokens per conversation from persona stats
 */
function deriveKvSessionCounts(
  request: NewSizingRequest,
  mcSummary: McSummaryLike,
) {
  const { p50Active, p95Active, personas } = mcSummary;

  const baseConcurrentUsers = request.baseConcurrentUsers ?? 0;

  // Important: use MC shape ratio p95 / p50, not p95 / baseConcurrentUsers
  const ratioActive = p50Active > 0 ? p95Active / p50Active : 1;

  // KV P50 pinned to slider
  const kvSessionsP50 = baseConcurrentUsers;

  // KV P95 scaled by MC shape
  const kvSessionsP95 = Math.round(kvSessionsP50 * ratioActive);

  const activePercentiles = mcSummary.activePercentiles ?? {};
  const p50Baseline = activePercentiles[50] ?? p50Active;

  const kvSessionsByPercentile: Record<McCurvePercentile, number> = {
    5: 0,
    25: 0,
    50: kvSessionsP50,
    75: 0,
    95: kvSessionsP95,
  };

  MC_CURVE_PERCENTILES.forEach((p) => {
    if (p === 50) return;
    const activeValue = activePercentiles[p];

    let scale = 1;
    if (activeValue && p50Baseline && p50Baseline > 0) {
      scale = activeValue / p50Baseline;
    } else if (p === 95) {
      scale = ratioActive;
    }

    kvSessionsByPercentile[p] = Math.max(0, Math.round(kvSessionsP50 * scale));
  });

  let avgTokensPerConversation = 0;

  if (Array.isArray(personas) && personas.length > 0) {
    avgTokensPerConversation = personas.reduce((sum, p) => {
      const tokensPerConversation =
        (p.inputTokensPerConversation ?? 0) +
        (p.outputTokensPerConversation ?? 0);
      return sum + tokensPerConversation * (p.shareOfTraffic ?? 0);
    }, 0);
  } else {
    const tokens = request.personas ?? [];
    if (tokens.length > 0) {
      const total = tokens.reduce((sum, p) => {
        const t =
          (p.inputTokensPerConversation ?? 0) + (p.outputTokensPerConversation ?? 0);
        return sum + t;
      }, 0);
      avgTokensPerConversation = total / tokens.length;
    }
  }

  console.log('[kvSizing] deriveKvSessionCounts output', {
    kvSessionsP50,
    kvSessionsP95,
    kvSessionsByPercentile,
    avgTokensPerConversation,
    p50Active,
    p95Active,
    activePercentiles,
    ratioActive,
    baseConcurrentUsers,
  });

  return {
    kvSessionsP50,
    kvSessionsP95,
    kvSessionsByPercentile,
    avgTokensPerConversation,
  };
}

/**
 * Strategy A: simple pooled full retention.
 */
export function sizeKvSimplePooled(
  request: NewSizingRequest,
  mcSummary: McSummaryLike,
): KvSizingResult {
  console.log('[kvSizing] sizeKvSimplePooled start', {
    baseConcurrentUsers: request.baseConcurrentUsers,
  });
  const {
    kvSessionsP50,
    kvSessionsP95,
    kvSessionsByPercentile,
    avgTokensPerConversation,
  } = deriveKvSessionCounts(request, mcSummary);

  const kvBytesPerToken = request.model.kvBytesPerToken ?? 0;
  const kvCapacityGiB = request.model.usableKvGiBPerGpu ?? 0;

  const kvGiBByPercentile: Record<McCurvePercentile, number> = {
    5: 0,
    25: 0,
    50: 0,
    75: 0,
    95: 0,
  };
  const kvGpusByPercentile: Record<McCurvePercentile, number> = {
    5: 0,
    25: 0,
    50: 0,
    75: 0,
    95: 0,
  };

  MC_CURVE_PERCENTILES.forEach((p) => {
    const sessions = kvSessionsByPercentile[p] ?? 0;
    const kvGiB = (sessions * avgTokensPerConversation * kvBytesPerToken) / BYTES_PER_GIB;
    kvGiBByPercentile[p] = kvGiB;
    kvGpusByPercentile[p] = kvCapacityGiB > 0 ? Math.ceil(kvGiB / kvCapacityGiB) : 0;
  });

  const kvGiBP50 = kvGiBByPercentile[50];
  const kvGiBP95 = kvGiBByPercentile[95];

  const kvGpusP50 = kvGpusByPercentile[50];
  const kvGpusP95 = kvGpusByPercentile[95];

  const result = {
    kvSessionsP50,
    kvSessionsP95,
    kvSessionsByPercentile,
    avgTokensPerConversation,
    kvBytesPerToken,
    kvGiBP50,
    kvGiBP95,
    kvCapacityGiB,
    kvGpusP50,
    kvGpusP95,
    kvGiBByPercentile,
    kvGpusByPercentile,
  };
  console.log('[kvSizing] sizeKvSimplePooled result', result);
  return result;
}

/**
 * Strategy B: pooled bin packing by persona KV footprint.
 */
export function sizeKvBinPackPooled(
  request: NewSizingRequest,
  mcSummary: McSummaryLike,
): KvSizingResult {
  console.log('[kvSizing] sizeKvBinPackPooled start', {
    baseConcurrentUsers: request.baseConcurrentUsers,
  });
  const {
    kvSessionsP50,
    kvSessionsP95,
    kvSessionsByPercentile,
    avgTokensPerConversation,
  } = deriveKvSessionCounts(request, mcSummary);

  const kvBytesPerToken = request.model.kvBytesPerToken ?? 0;
  const kvCapacityGiB = request.model.usableKvGiBPerGpu ?? 0;

  if (kvCapacityGiB <= 0 || kvBytesPerToken <= 0) {
    return sizeKvSimplePooled(request, mcSummary);
  }

  const personas = mcSummary.personas ?? [];

  if (personas.length === 0) {
    return sizeKvSimplePooled(request, mcSummary);
  }

  const kvGiBByPercentile: Record<McCurvePercentile, number> = {
    5: 0,
    25: 0,
    50: 0,
    75: 0,
    95: 0,
  };
  const kvGpusByPercentile: Record<McCurvePercentile, number> = {
    5: 0,
    25: 0,
    50: 0,
    75: 0,
    95: 0,
  };

  const computeBinPack = (targetSessions: number) => {
    const sessionSizes: number[] = [];

    for (const p of personas) {
      const tokensPerConversation =
        (p.inputTokensPerConversation ?? 0) +
        (p.outputTokensPerConversation ?? 0);
      const kvGiBPerSession =
        (tokensPerConversation * kvBytesPerToken) / BYTES_PER_GIB;

      if (kvGiBPerSession <= 0) continue;

      const share = p.shareOfTraffic ?? 0;
      const sessionsForPersona = Math.round(targetSessions * share);
      if (sessionsForPersona <= 0) continue;

      for (let j = 0; j < sessionsForPersona; j++) {
        sessionSizes.push(kvGiBPerSession);
      }
    }

    if (sessionSizes.length === 0) {
      return { kvGiB: 0, gpus: 0 };
    }

    if (sessionSizes.length > targetSessions * 2) {
      sessionSizes.length = targetSessions;
    }

    sessionSizes.sort((a, b) => b - a);

    const gpuRemaining: number[] = [];
    let kvGiBTotal = 0;

    for (const size of sessionSizes) {
      kvGiBTotal += size;
      let placed = false;
      for (let i = 0; i < gpuRemaining.length; i++) {
        if (gpuRemaining[i] >= size) {
          gpuRemaining[i] -= size;
          placed = true;
          break;
        }
      }
      if (!placed) {
        gpuRemaining.push(Math.max(0, kvCapacityGiB - size));
      }
    }

    return { kvGiB: kvGiBTotal, gpus: gpuRemaining.length };
  };

  MC_CURVE_PERCENTILES.forEach((p) => {
    const targetSessions = kvSessionsByPercentile[p] ?? 0;
    if (targetSessions <= 0) {
      kvGiBByPercentile[p] = 0;
      kvGpusByPercentile[p] = 0;
      return;
    }

    const { kvGiB, gpus } = computeBinPack(targetSessions);
    kvGiBByPercentile[p] = kvGiB;
    kvGpusByPercentile[p] = gpus;
  });

  // Derive p50 via average tokens to preserve the original intent of using the base slider value
  const kvGiBP50 =
    (kvSessionsP50 * avgTokensPerConversation * kvBytesPerToken) /
    BYTES_PER_GIB;
  const kvGpusP50 =
    kvCapacityGiB > 0 ? Math.ceil(kvGiBP50 / kvCapacityGiB) : 0;

  kvGiBByPercentile[50] = kvGiBP50;
  kvGpusByPercentile[50] = kvGpusP50;

  const kvGiBP95 = kvGiBByPercentile[95];
  const kvGpusP95 = kvGpusByPercentile[95];

  const result = {
    kvSessionsP50,
    kvSessionsP95,
    kvSessionsByPercentile,
    avgTokensPerConversation,
    kvBytesPerToken,
    kvGiBP50,
    kvGiBP95,
    kvCapacityGiB,
    kvGpusP50,
    kvGpusP95,
    kvGiBByPercentile,
    kvGpusByPercentile,
  };
  console.log('[kvSizing] sizeKvBinPackPooled result', result);
  return result;
}

/**
 * Strategy C: persona dedicated packing.
 */
export function sizeKvPersonaDedicated(
  request: NewSizingRequest,
  mcSummary: McSummaryLike,
): KvSizingResult {
  console.log('[kvSizing] sizeKvPersonaDedicated start', {
    baseConcurrentUsers: request.baseConcurrentUsers,
  });
  const {
    kvSessionsP50,
    kvSessionsP95,
    kvSessionsByPercentile,
    avgTokensPerConversation,
  } = deriveKvSessionCounts(request, mcSummary);

  const kvBytesPerToken = request.model.kvBytesPerToken ?? 0;
  const kvCapacityGiB = request.model.usableKvGiBPerGpu ?? 0;

  if (kvCapacityGiB <= 0 || kvBytesPerToken <= 0) {
    return sizeKvSimplePooled(request, mcSummary);
  }

  const personas = mcSummary.personas ?? [];

  if (personas.length === 0) {
    return sizeKvSimplePooled(request, mcSummary);
  }

  const kvGiBByPercentile: Record<McCurvePercentile, number> = {
    5: 0,
    25: 0,
    50: 0,
    75: 0,
    95: 0,
  };
  const kvGpusByPercentile: Record<McCurvePercentile, number> = {
    5: 0,
    25: 0,
    50: 0,
    75: 0,
    95: 0,
  };

  const computeDedicated = (targetSessions: number) => {
    let kvGiBTotal = 0;
    let gpuCount = 0;

    for (const p of personas) {
      const share = p.shareOfTraffic ?? 0;
      if (share <= 0) continue;

      const personaSessions = Math.round(targetSessions * share);
      if (personaSessions <= 0) continue;

      const tokensPerConversation =
        (p.inputTokensPerConversation ?? 0) +
        (p.outputTokensPerConversation ?? 0);
      const kvGiBPersona =
        (personaSessions *
          tokensPerConversation *
          kvBytesPerToken) /
        BYTES_PER_GIB;

      kvGiBTotal += kvGiBPersona;
      gpuCount += Math.ceil(kvGiBPersona / kvCapacityGiB);
    }

    return { kvGiBTotal, gpuCount };
  };

  MC_CURVE_PERCENTILES.forEach((p) => {
    const targetSessions = kvSessionsByPercentile[p] ?? 0;
    if (targetSessions <= 0) {
      kvGiBByPercentile[p] = 0;
      kvGpusByPercentile[p] = 0;
      return;
    }
    const { kvGiBTotal, gpuCount } = computeDedicated(targetSessions);
    kvGiBByPercentile[p] = kvGiBTotal;
    kvGpusByPercentile[p] = gpuCount;
  });

  const kvGiBP50 =
    (kvSessionsP50 * avgTokensPerConversation * kvBytesPerToken) /
    BYTES_PER_GIB;
  const kvGpusP50 =
    kvCapacityGiB > 0 ? Math.ceil(kvGiBP50 / kvCapacityGiB) : 0;

  kvGiBByPercentile[50] = kvGiBP50;
  kvGpusByPercentile[50] = kvGpusP50;

  const kvGiBP95 = kvGiBByPercentile[95];
  const kvGpusP95 = kvGpusByPercentile[95];

  const result = {
    kvSessionsP50,
    kvSessionsP95,
    kvSessionsByPercentile,
    avgTokensPerConversation,
    kvBytesPerToken,
    kvGiBP50,
    kvGiBP95,
    kvCapacityGiB,
    kvGpusP50,
    kvGpusP95,
    kvGiBByPercentile,
    kvGpusByPercentile,
  };
  console.log('[kvSizing] sizeKvPersonaDedicated result', result);
  return result;
}
